import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Printer, ChevronLeft } from "lucide-react";

// Code39 minimal (chars yang dipakai no. pesanan: A-Z 0-9 - spasi)
const C39 = {
  "0": "nnnwwnwnn", "1": "wnnwnnnnw", "2": "nnwwnnnnw", "3": "wnwwnnnnn", "4": "nnnwwnnnw",
  "5": "wnnwwnnnn", "6": "nnwwwnnnn", "7": "nnnwnnwnw", "8": "wnnwnnwnn", "9": "nnwwnnwnn",
  A: "wnnnnwnnw", B: "nnwnnwnnw", C: "wnwnnwnnn", D: "nnnnwwnnw", E: "wnnnwwnnn", F: "nnwnwwnnn",
  G: "nnnnnwwnw", H: "wnnnnwwnn", I: "nnwnnwwnn", J: "nnnnwwwnn", K: "wnnnnnnww", L: "nnwnnnnww",
  M: "wnwnnnnwn", N: "nnnnwnnww", O: "wnnnwnnwn", P: "nnwnwnnwn", Q: "nnnnnnwww", R: "wnnnnnwwn",
  S: "nnwnnnwwn", T: "nnnnwnwwn", U: "wwnnnnnnw", V: "nwwnnnnnw", W: "wwwnnnnnn", X: "nwnnwnnnw",
  Y: "wwnnwnnnn", Z: "nwwnwnnnn", "-": "nwnnnnwnw", ".": "wwnnnnwnn", " ": "nwwnnnwnn", "*": "nwnnwnwnn",
};

function Barcode({ text, height = 40 }) {
  const seq = `*${String(text).toUpperCase().replace(/[^0-9A-Z\-. ]/g, "")}*`;
  const N = 2, W = 5, GAP = 2;
  let x = 0;
  const bars = [];
  for (const ch of seq) {
    const pat = C39[ch] || C39["-"];
    for (let i = 0; i < 9; i++) {
      const w = pat[i] === "w" ? W : N;
      if (i % 2 === 0) bars.push(<rect key={bars.length} x={x} y={0} width={w} height={height} fill="#000" />);
      x += w;
    }
    x += GAP;
  }
  return <svg viewBox={`0 0 ${x} ${height}`} preserveAspectRatio="none" style={{ width: "100%", height }}>{bars}</svg>;
}

export default function AdminLabelPrint() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/admin/orders/${orderId}`).then((r) => setO(r.data)).catch(() => setErr("Pesanan tidak ditemukan atau akses ditolak."));
  }, [orderId]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">{err}</div>;
  if (!o) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Memuat label...</div>;

  const parts = (o.address || "").split(" • ");
  const recvName = parts[0] || o.customer_name || "-";
  const recvPhone = parts[1] || "-";
  const recvAddr = parts.slice(2).join(" • ") || "-";
  const si = o.shipping_info || {};
  const weight = si.weight_grams || Math.max(1, (o.items || []).reduce((s, it) => s + it.qty, 0)) * 400;
  const totalQty = (o.items || []).reduce((s, it) => s + it.qty, 0);
  const isCod = o.payment_method === "cod";
  const now = new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta" });

  return (
    <div className="min-h-screen bg-slate-200 py-6 flex flex-col items-center">
      <style>{`
        @page { size: 105mm 148mm; margin: 0; }
        @media print {
          body * { visibility: hidden; }
          .label-sheet, .label-sheet * { visibility: visible; }
          .label-sheet { position: absolute; left: 0; top: 0; margin: 0; box-shadow: none; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print mb-4 flex items-center gap-3">
        <button data-testid="label-back" onClick={() => nav(-1)} className="h-9 px-4 rounded-lg bg-white border border-slate-300 text-xs font-semibold flex items-center gap-1.5">
          <ChevronLeft className="w-4 h-4" /> Kembali
        </button>
        <button data-testid="label-print-btn" onClick={() => window.print()} className="h-9 px-5 rounded-lg bg-violet-600 text-white text-xs font-semibold flex items-center gap-1.5">
          <Printer className="w-4 h-4" /> Print Label (A6)
        </button>
        <span className="text-[11px] text-slate-500">Ukuran kertas A6 (105 × 148 mm). Mencetak label tidak mengubah status pesanan.</span>
      </div>

      <div className="label-sheet bg-white text-black shadow-lg" data-testid="label-sheet"
        style={{ width: "105mm", minHeight: "148mm", padding: "4mm", fontFamily: "Arial, sans-serif", fontSize: "9pt" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "2px solid #000", paddingBottom: "2mm" }}>
          <div style={{ fontWeight: 900, fontSize: "16pt", letterSpacing: "0.5px" }}>KIYANA</div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontWeight: 800, fontSize: "12pt", background: "#000", color: "#fff", padding: "1mm 3mm" }}>{si.courier || o.courier || "KURIR"} {si.service || ""}</div>
            <div style={{ fontSize: "7.5pt", marginTop: "0.5mm" }}>{si.service_name || o.courier || "Pengiriman manual"}</div>
          </div>
        </div>

        {/* Barcode no pesanan */}
        <div style={{ borderBottom: "1px solid #000", padding: "2mm 0" }}>
          <div data-testid="label-barcode"><Barcode text={o.code} height={42} /></div>
          <div style={{ textAlign: "center", fontWeight: 700, letterSpacing: "1px", marginTop: "1mm" }}>{o.code}</div>
        </div>

        {/* No resi kosong */}
        <div style={{ border: "1.5px dashed #000", margin: "2mm 0", padding: "2mm", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 800 }}>No. Resi:</span>
          <span style={{ fontSize: "7.5pt", fontStyle: "italic" }}>(kosong — diisi manual oleh admin)</span>
        </div>

        {/* Penerima / Pengirim */}
        <div style={{ display: "flex", gap: "3mm", borderBottom: "1px solid #000", paddingBottom: "2mm" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800 }}>Penerima: {recvName}</div>
            <div>{recvPhone}</div>
            <div style={{ marginTop: "1mm" }}>{recvAddr}</div>
          </div>
          <div style={{ width: "34mm", fontSize: "8pt" }}>
            <div style={{ fontWeight: 800 }}>Pengirim:</div>
            <div>Kiyana by Cirebon Cloth</div>
            <div>Kota Cirebon, Jawa Barat 45111</div>
          </div>
        </div>

        {/* Info paket */}
        <div style={{ display: "flex", gap: "2mm", margin: "2mm 0" }}>
          <div style={{ flex: 1, border: "1px solid #000", padding: "1.5mm", fontWeight: 700 }}>Berat: {weight} gr</div>
          <div style={{ flex: 2, border: "1px solid #000", padding: "1.5mm", fontWeight: 700 }}>
            {isCod ? `COD: ${formatIDR(o.total)}` : "NON-COD — Penjual tidak perlu bayar ongkir ke kurir"}
          </div>
        </div>
        <div style={{ marginBottom: "2mm" }}>No. Pesanan: <b>{o.code}</b></div>

        {/* Daftar produk */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "8pt" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #000", padding: "1mm", width: "6mm" }}>#</th>
              <th style={{ border: "1px solid #000", padding: "1mm", textAlign: "left" }}>Produk</th>
              <th style={{ border: "1px solid #000", padding: "1mm", textAlign: "left", width: "30mm" }}>Variasi</th>
              <th style={{ border: "1px solid #000", padding: "1mm", width: "8mm" }}>Qty</th>
            </tr>
          </thead>
          <tbody>
            {(o.items || []).map((it, i) => (
              <tr key={i}>
                <td style={{ border: "1px solid #000", padding: "1mm", textAlign: "center" }}>{i + 1}</td>
                <td style={{ border: "1px solid #000", padding: "1mm" }}>{it.name}</td>
                <td style={{ border: "1px solid #000", padding: "1mm" }}>{it.variant || "-"}</td>
                <td style={{ border: "1px solid #000", padding: "1mm", textAlign: "center", fontWeight: 700 }}>{it.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2mm", fontSize: "8pt" }}>
          <span>Store Name: <b>Kiyana by Cirebon Cloth</b></span>
          <span>Total: <b>{totalQty}</b></span>
        </div>
        <div style={{ fontSize: "7pt", marginTop: "1mm", color: "#333" }}>Dicetak: {now} WIB — Sistem Kiyana Management</div>
      </div>
    </div>
  );
}
